export const BONUS_CARD_DENOMINATIONS = [50, 100, 200, 500, 1000] as const;

export type BonusCardDenomination = (typeof BONUS_CARD_DENOMINATIONS)[number];
export type BonusCardMotif = 'sunburst' | 'starfield' | 'tide' | 'facets' | 'orbit';
export type BonusCardFoil = 'champagne' | 'rose' | 'aqua' | 'lime' | 'violet';
export type BonusCardSeed = number | string;

export type BonusCardPalette = Readonly<{
  base: string;
  deep: string;
  bright: string;
  ink: string;
  paper: string;
  foil: string;
  edge: string;
}>;

export type BonusCardVariantDefinition = Readonly<{
  id: string;
  denomination: BonusCardDenomination;
  denominationLabel: string;
  displayName: string;
  themeName: string;
  headline: string;
  rewardLabel: string;
  strapline: string;
  motif: BonusCardMotif;
  foil: BonusCardFoil;
  palette: BonusCardPalette;
  artSeed: number;
}>;

export const BONUS_CARD_VARIANTS = [
  {
    id: 'sun-pop-50',
    denomination: 50,
    denominationLabel: '50',
    displayName: 'Sun Pop 50',
    themeName: 'Sun Pop',
    headline: 'QUICK BONUS',
    rewardLabel: 'PRIZE CREDITS',
    strapline: 'BRIGHT START',
    motif: 'sunburst',
    foil: 'champagne',
    palette: {
      base: '#f06b1a',
      deep: '#9d2819',
      bright: '#ffcf3f',
      ink: '#5a1617',
      paper: '#fff4c7',
      foil: '#ffd77b',
      edge: '#b63d19',
    },
    artSeed: 0x41d9a31b,
  },
  {
    id: 'star-pulse-100',
    denomination: 100,
    denominationLabel: '100',
    displayName: 'Star Pulse 100',
    themeName: 'Star Pulse',
    headline: 'STAR BONUS',
    rewardLabel: 'PRIZE CREDITS',
    strapline: 'LUCK IN MOTION',
    motif: 'starfield',
    foil: 'rose',
    palette: {
      base: '#d92f84',
      deep: '#751651',
      bright: '#ff80bb',
      ink: '#4f123c',
      paper: '#fff1f7',
      foil: '#ffb1d5',
      edge: '#9d205f',
    },
    artSeed: 0xb73cb56d,
  },
  {
    id: 'tidal-neon-200',
    denomination: 200,
    denominationLabel: '200',
    displayName: 'Tidal Neon 200',
    themeName: 'Tidal Neon',
    headline: 'WAVE BONUS',
    rewardLabel: 'PRIZE CREDITS',
    strapline: 'MAKE A SPLASH',
    motif: 'tide',
    foil: 'aqua',
    palette: {
      base: '#0ba9bd',
      deep: '#075470',
      bright: '#6ce9e1',
      ink: '#073e51',
      paper: '#edffff',
      foil: '#9bfff5',
      edge: '#087c91',
    },
    artSeed: 0x0c9e71f4,
  },
  {
    id: 'emerald-facet-500',
    denomination: 500,
    denominationLabel: '500',
    displayName: 'Emerald Facet 500',
    themeName: 'Emerald Facet',
    headline: 'GEM BONUS',
    rewardLabel: 'PRIZE CREDITS',
    strapline: 'STACK THE SHINE',
    motif: 'facets',
    foil: 'lime',
    palette: {
      base: '#41aa55',
      deep: '#14543b',
      bright: '#b8ef53',
      ink: '#123d2f',
      paper: '#f3ffd1',
      foil: '#dcff7a',
      edge: '#26783f',
    },
    artSeed: 0x79e6d2a3,
  },
  {
    id: 'cosmic-orbit-1000',
    denomination: 1000,
    denominationLabel: '1,000',
    displayName: 'Cosmic Orbit 1000',
    themeName: 'Cosmic Orbit',
    headline: 'GRAND BONUS',
    rewardLabel: 'PRIZE CREDITS',
    strapline: 'A RARE FIND',
    motif: 'orbit',
    foil: 'violet',
    palette: {
      base: '#6846d9',
      deep: '#28236f',
      bright: '#d15cff',
      ink: '#211957',
      paper: '#fbf0ff',
      foil: '#e6adff',
      edge: '#4431a8',
    },
    artSeed: 0xe5733fc1,
  },
] as const satisfies readonly BonusCardVariantDefinition[];

export type BonusCardVariant = (typeof BONUS_CARD_VARIANTS)[number];
export type BonusCardVariantId = BonusCardVariant['id'];

const VARIANTS_BY_ID = new Map<BonusCardVariantId, BonusCardVariant>(
  BONUS_CARD_VARIANTS.map((variant) => [variant.id, variant]),
);

export function getBonusCardVariant(id: BonusCardVariantId): BonusCardVariant {
  const variant = VARIANTS_BY_ID.get(id);
  if (!variant) throw new Error(`Unknown bonus card variant: ${id}`);
  return variant;
}

export function getBonusCardVariantForDenomination(
  denomination: BonusCardDenomination,
): BonusCardVariant {
  const variant = BONUS_CARD_VARIANTS.find((candidate) => candidate.denomination === denomination);
  if (!variant) throw new Error(`No bonus card variant for denomination: ${denomination}`);
  return variant;
}

export function bonusCardSeedToUint32(seed: BonusCardSeed): number {
  if (typeof seed === 'number') {
    if (!Number.isFinite(seed)) throw new Error('Bonus card seed must be finite.');
    return Math.trunc(seed) >>> 0;
  }

  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function pickBonusCardVariant(
  seed: BonusCardSeed,
  allowedIds: readonly BonusCardVariantId[] = BONUS_CARD_VARIANTS.map((variant) => variant.id),
): BonusCardVariant {
  if (allowedIds.length === 0) throw new Error('At least one bonus card variant must be allowed.');
  const index = bonusCardSeedToUint32(seed) % allowedIds.length;
  return getBonusCardVariant(allowedIds[index]);
}

export function createBonusCardVariantSequence(
  seed: BonusCardSeed,
  count: number,
  allowedIds: readonly BonusCardVariantId[] = BONUS_CARD_VARIANTS.map((variant) => variant.id),
): BonusCardVariant[] {
  if (!Number.isInteger(count) || count < 0) {
    throw new Error('Bonus card sequence count must be a non-negative integer.');
  }
  if (allowedIds.length === 0) throw new Error('At least one bonus card variant must be allowed.');

  const result: BonusCardVariant[] = [];
  let state = bonusCardSeedToUint32(seed) || 0x6d2b79f5;
  for (let index = 0; index < count; index += 1) {
    state += 0x6d2b79f5;
    let random = state;
    random = Math.imul(random ^ (random >>> 15), random | 1);
    random ^= random + Math.imul(random ^ (random >>> 7), random | 61);
    random = (random ^ (random >>> 14)) >>> 0;
    result.push(getBonusCardVariant(allowedIds[random % allowedIds.length]));
  }
  return result;
}
